/**
 * Counter used by {uniqueId} to iterate.
 * @private
 * @type {Number}
 */
let idCounter = 0;

/**
 * Generates identifiers unique to a single browser session. Each time it runs,
 * the integer at the end of the return value will increment.
 *
 * Borrowed from underscore.js: http://underscorejs.org/#uniqueId
 *
 * @param  {String} [prefix] Optional prefix for identifier
 * @return {String}
 * @example
 *
 * var identifier = uniqueId();
 * // returns '1'
 *
 * var prefixedIdentifier = uniqueId('label-');
 * // returns 'label-2'
 */
export function uniqueId(prefix) {
  const id = ++idCounter + '';
  return prefix ? prefix + id : id;
}

/**
 * A helper for asynchronously loading scripts.
 *
 * Provides a Promise interface for passing a function that will only be called
 * if the script is successfully added to the page.
 *
 * @private
 * @param {String} url The URL for the script to be loaded.
 * @returns {Promise}
 * @example
 *
 * loadScript('backup.js').then(() => {
 *   // anything that depends on that script loading
 * });
 */
export function loadScript(url) {
  return new Promise((resolve, reject) => {
    // create the `script` element
    const script = document.createElement('script');

    // set its URL
    script.src = url;

    // should always be async
    script.async = true;

    // resolve the Promise on load
    script.onload = resolve;

    // reject the Promise if there is an error
    script.onerror = reject;

    // attach the script to the document body
    document.body.appendChild(script);
  });
}

/**
 * Pre-defined regex for parsing out a cookie's value.
 *
 * @private
 * @type {RegExp}
 */
const cookieRe = /(%[\dA-F]{2})+/gi;

/**
 * Decodes a portion of a cookie's value with a regex customized for cookie
 * strings.
 *
 * @private
 * @param {string} value
 */
function decodeCookieValue(value) {
  return value.replace(cookieRe, decodeURIComponent);
}

/**
 * Returns the value of the provided cookie from the document, if it exists.
 * Otherwise returns "undefined".
 *
 * @param {string} name
 * @returns {string|undefined}
 */
export function getCookie(name) {
  // if there is no document, there are no cookies
  if (typeof document === 'undefined') {
    return undefined;
  }

  const cookies = document.cookie ? document.cookie.split('; ') : [];

  for (let idx = 0; idx < cookies.length; idx++) {
    // the raw individual cookie string
    const raw = cookies[idx];

    // break the cookie into parts
    const parts = raw.split('=');

    // prepare the key
    const key = decodeCookieValue(parts[0]);

    // we have a match!
    if (key === name) {
      // put the cookie back together again
      let value = parts.slice(1).join('=');

      // if the value is wrapped in quotes, peel them off
      if (value[0] === '"') {
        value = value.slice(1, -1);
      }

      // return the decoded value
      return decodeCookieValue(value);
    }
  }

  // never found a match
  return undefined;
}
